// Pure helpers for the Lead Agent confirmation + success modals (RET-1230 /
// C2, Unit 5). Kept out of the components for testability and the
// react-refresh/only-export-components rule.

import { ePlanFrequency } from 'src/providers/SubscriptionProvider/Subscription.enums';
import { formatPrice } from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.functions';
import type {
  tRelatedProduct,
  tRenewalProjectionWireData,
} from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.types';
import type {
  tLeadAgentAcceptance,
  tLeadAgentSelectableTier,
} from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.types';
import { formatPaymentDate } from '../LeadAgentAcceptanceBanner.functions';

/**
 * Frequency token used in the Lead Agent SKU `code`
 * (`LEAD_AGENT_{TIER}_{FREQTOKEN}_{CURRENCY}`). Mirrors the leadbilling
 * seeder (A1 / RET-1217): MONTHLY / QUARTERLY / BIANNUAL / ANNUALLY.
 * The token derives from the workspace's plan frequency — the Owner never
 * picks a periodicity (it is inherited from the Pro/Advanced plan).
 */
const FREQUENCY_TOKEN_BY_PLAN_FREQUENCY: Partial<
  Record<ePlanFrequency, string>
> = {
  [ePlanFrequency.MONTHLY]: 'MONTHLY',
  [ePlanFrequency.QUARTERLY]: 'QUARTERLY',
  [ePlanFrequency.BIANNUAL]: 'BIANNUAL',
  [ePlanFrequency.ANNUALLY]: 'ANNUALLY',
};

/**
 * The renewal projection's `nextPaymentAt` means ONE thing: the date of the
 * DEFERRED first Lead Agent charge. When it is set AND the first charge is a
 * FULL one, A6 aligns the displayed "Tu próxima renovación" to it (the RET-1299
 * override), because that is the renewal where the add-on lands.
 *
 * That premise breaks for a MONTHLY plan once it has renewed. There,
 * reactivating/accepting charges the prorated remainder TODAY (RET-2129) and the
 * add-on then recurs on the plan's own renewal — there is no deferred charge, so
 * passing `nextPaymentAt` made A6 echo `trial_end` back as `renewal_date`. The
 * modal printed it as the renewal and misdated the monthly total with it
 * (observed: "Total mensual a partir del 19 de sep." on a subscription renewing
 * 14 de octubre — 25 days off; it also implies the wrong token-reset date, since
 * the cap resets on the plan renewal per MLA-25/MLA-55). Returning null makes A6
 * fall back to the real Stripe `current_period_end`.
 *
 * NON-MONTHLY is deliberately untouched (RET-2129 R4 keeps its behaviour): there
 * the first charge is a BRIDGE, the RET-1299 override never fires, and
 * `nextPaymentAt` is what sizes the bridge — dropping it would collapse the
 * bridge to `full` and hide the modal's first-charge block.
 *
 * An ABSENT phase also keeps the previous behaviour: the same rollout fallback
 * the rest of the provider uses while bff-web ships the field.
 */
export function resolveDeferredFirstChargeAt(
  acceptance: tLeadAgentAcceptance | null | undefined,
  frequency: ePlanFrequency | null | undefined,
): string | null {
  if (!acceptance) return null;
  const deferred = acceptance.nextPaymentAt ?? null;

  // Only the monthly rail charges immediately after the renewal (R4).
  if (frequency !== ePlanFrequency.MONTHLY) return deferred;

  const phase = acceptance.phase;
  if (phase && phase !== 'before_renewal') return null;

  return deferred;
}

/** All three selectable tiers, in display order (Starter, Growth, Scale). */
export const LEAD_AGENT_TIER_ORDER: readonly tLeadAgentSelectableTier[] = [
  'STARTER',
  'GROWTH',
  'SCALE',
];

export type tLeadAgentTierProduct = {
  tier: tLeadAgentSelectableTier;
  product: tRelatedProduct;
};

/** Reads the stable machine code from a rejected BFF response, if present. */
export function readLeadAgentErrorCode(error: unknown): string | null {
  const payload = (error as { response?: { data?: unknown } } | undefined)
    ?.response?.data;
  if (typeof payload !== 'object' || payload === null) return null;

  const body = payload as Record<string, unknown>;
  if (typeof body.code === 'string') return body.code;

  // Rolling-deploy compatibility: older BFFs forward Laravel's envelope
  // unchanged, where Lead Agent machine codes live under data.code.
  if (typeof body.data !== 'object' || body.data === null) return null;
  const nestedCode = (body.data as Record<string, unknown>).code;
  return typeof nestedCode === 'string' ? nestedCode : null;
}

/**
 * Builds the exact Lead Agent SKU code for a tier given the workspace's plan
 * frequency and currency: `LEAD_AGENT_{TIER}_{FREQTOKEN}_{CURRENCY}`. Returns
 * null when the frequency is not one of the four billable periodicities (e.g.
 * trial / weekly) so callers degrade to "catalog not available".
 */
export function buildLeadAgentProductCode(
  tier: tLeadAgentSelectableTier,
  options: { frequency: ePlanFrequency; currency: string },
): string | null {
  const token = FREQUENCY_TOKEN_BY_PLAN_FREQUENCY[options.frequency];
  const currency = options.currency?.toUpperCase();
  if (!token || !currency) return null;
  return `LEAD_AGENT_${tier}_${token}_${currency}`;
}

/**
 * Resolves the catalog product for a single tier by exact `code` match against
 * the related + available-for-purchase products of the workspace. Returns null
 * when the SKU is absent (catalog not seeded) — the caller surfaces an error
 * state and disables confirm (never invents an amount).
 */
export function findLeadAgentProductForTier(
  tier: tLeadAgentSelectableTier,
  products: tRelatedProduct[],
  options: { frequency: ePlanFrequency; currency: string },
): tRelatedProduct | null {
  const code = buildLeadAgentProductCode(tier, options);
  if (!code) return null;
  return products.find((product) => product.code === code) ?? null;
}

/**
 * Resolves every tier that has a catalog product for the workspace's
 * frequency + currency, preserving Starter / Growth / Scale order. Tiers
 * whose SKU is missing are omitted. The suggested tier may still render its
 * row even if absent here (handled by the modal), but it cannot be confirmed.
 */
export function resolveTierProducts(
  products: tRelatedProduct[],
  options: { frequency: ePlanFrequency; currency: string },
): tLeadAgentTierProduct[] {
  return LEAD_AGENT_TIER_ORDER.map((tier) => {
    const product = findLeadAgentProductForTier(tier, products, options);
    return product ? { tier, product } : null;
  }).filter((entry): entry is tLeadAgentTierProduct => entry !== null);
}

/**
 * Per-period price suffix for a tier price ("/mes", "/trimestre", "/semestre",
 * "/año"). The amount itself is the catalog `price` (the full charge for that
 * period), so the suffix only labels the cadence inherited from the plan.
 */
export function periodSuffixForFrequency(frequency: ePlanFrequency): string {
  switch (frequency) {
    case ePlanFrequency.MONTHLY:
      return '/mes';
    case ePlanFrequency.QUARTERLY:
      return '/trimestre';
    case ePlanFrequency.BIANNUAL:
      return '/semestre';
    case ePlanFrequency.ANNUALLY:
      return '/año';
    default:
      return '';
  }
}

/**
 * Formats a tier's catalog price with its period suffix, e.g. "$29.00/mes".
 * The catalog `price` is already in cents (same unit as `formatPrice`).
 */
export function formatTierPrice(
  product: tRelatedProduct,
  frequency: ePlanFrequency,
): string {
  const base = formatPrice({
    price: product.price,
    currency: product.currency,
  });
  return `${base}${periodSuffixForFrequency(frequency)}`;
}

/**
 * Reactivation card price (RET-1417, Option A). Derives the selected tier's
 * renewal price from the projection's Lead Agent line (cents + the projection
 * currency) rather than a separate `getProducts` fetch, and appends the period
 * suffix inherited from the plan frequency, e.g. "$348.00 USD/año". The
 * projection only carries the SELECTED tier's line, so the caller renders this
 * on the selected tier row only.
 */
export function formatLeadAgentRenewalPrice(
  projection: tRenewalProjectionWireData,
  frequency: ePlanFrequency,
): string {
  const base = formatPrice({
    price: projection.leadAgentLine.amountCents,
    currency: projection.currency,
  });
  return `${base}${periodSuffixForFrequency(frequency)}`;
}

/**
 * Validates the projection wire's `frequency` string against the known plan
 * frequencies (runtime-checked, never an `as`-cast of external input). Returns
 * null for an unknown/absent value so the caller falls back. Used so the
 * renewal price suffix + total period word derive from the SAME source as the
 * amounts (the projection), not a divergent subscription default.
 */
export function parsePlanFrequency(
  value: string | null | undefined,
): ePlanFrequency | null {
  if (!value) return null;
  return (Object.values(ePlanFrequency) as string[]).includes(value)
    ? (value as ePlanFrequency)
    : null;
}

/**
 * i18n key suffix for the renewal-total period adjective ("Total {anual}…"),
 * so the word is translated per locale (`nextRenewal.period.<KEY>`) instead of
 * baked in one language. Null for non-billable periodicities, so the caller
 * drops the period word and degrades to a plain "Total".
 */
export function renewalPeriodKey(
  frequency: ePlanFrequency,
): 'MONTHLY' | 'QUARTERLY' | 'BIANNUAL' | 'ANNUALLY' | null {
  switch (frequency) {
    case ePlanFrequency.MONTHLY:
      return 'MONTHLY';
    case ePlanFrequency.QUARTERLY:
      return 'QUARTERLY';
    case ePlanFrequency.BIANNUAL:
      return 'BIANNUAL';
    case ePlanFrequency.ANNUALLY:
      return 'ANNUALLY';
    default:
      return null;
  }
}

/**
 * Reactivation explanatory-copy variant (RET-1417), keyed off the authoritative
 * `firstCharge.kind` (the same signal the modal already trusts elsewhere):
 * - `full`   → the plan already renewed; the Lead Agent is charged today and
 *              from then on renews bundled with the plan.
 * - `bridge` → a prorated/bridge charge today; the Lead Agent folds into the
 *              plan renewal on the renewal date.
 */
export function resolveReactivateCopyVariant(
  projection: tRenewalProjectionWireData,
): 'full' | 'bridge' {
  return projection.firstCharge.kind;
}

/**
 * Whether today's reactivation charge (the A11 preview amount, in cents) is a
 * partial/prorated charge that differs from the tier's full renewal price — the
 * signal to show the proration clarification line. Same-currency comparison
 * (both come from the same workspace subscription).
 */
export function isReactivateChargeProrated(
  todayChargeCents: number,
  projection: tRenewalProjectionWireData,
): boolean {
  return todayChargeCents !== projection.leadAgentLine.amountCents;
}

export type tRenewalBreakdownLine = {
  /** Display label, e.g. "Plan Growth" or "Plan Growth ×2". */
  description: string;
  /** Formatted amount in the projection currency, e.g. "$297.00 USD". */
  amount: string;
  /** Marks the Lead Agent line so the UI badges it "NUEVO". */
  isNew: boolean;
};

export type tRenewalFirstCharge = {
  /** Formatted first-charge amount in the projection currency. */
  amount: string;
  /** Personal charge date formatted es-MX, or null when not anchored. */
  date: string | null;
  /**
   * RET-1290 first-charge kind: `bridge` (presale non-monthly — a one-time
   * bridge that covers the Lead Agent until the renewal, shown as its own
   * "primer cargo" section) or `full` (monthly / post-launch — the Lead Agent
   * is charged in full at the next renewal, so no separate bridge section).
   */
  kind: 'full' | 'bridge';
  /** Whole months the bridge covers (0 for full). */
  months: number;
  /** True for a one-time prorated first charge (non-monthly periodicities). */
  prorated: boolean;
  /**
   * Legacy flag, always false now (the bridge amount is exact). Kept until the
   * wire drops it; the modal no longer renders an "estimado" legend.
   */
  estimated: boolean;
};

/**
 * View-model of the renewal projection (RET-1287). Every amount is formatted
 * from the backend's cents using the projection's currency — the modal never
 * does client-side arithmetic. `renewalLines` are the current plan/seats/addons
 * plus the new Lead Agent line (badged NUEVO); `renewalTotal` is the backend's
 * `projectedTotalCents`; `firstCharge` is the exact (or estimated) first charge.
 */
export type tRenewalBreakdown = {
  renewalLines: tRenewalBreakdownLine[];
  renewalTotal: string;
  firstCharge: tRenewalFirstCharge;
  /** Renewal date as the backend resolved it, formatted es-MX, or null. */
  renewalDate: string | null;
};

/** Appends a "×N" quantity suffix to a line name when the quantity is > 1. */
function describeRenewalLine(name: string, quantity: number): string {
  return quantity > 1 ? `${name} ×${quantity}` : name;
}

export type tRenewalBreakdownOptions = {
  /**
   * How to name the Lead Agent line, already translated by the caller.
   *
   * Without it the line falls back to `Lead Agent ${tier}` — the RAW billing
   * enum, which reaches the customer as "Lead Agent GROWTH" shouting next to
   * the catalog's own "Lead Agent Growth". Callers that have a `t` should pass
   * the resolved name; the fallback stays only so a caller without one keeps
   * rendering a line instead of a blank.
   */
  leadAgentLabel?: string | null;
};

/** The Lead Agent line's display name: the caller's resolved one, or the raw
 *  enum as a last resort. Kept in one place so both builders agree. */
function resolveLeadAgentLabel(
  tier: string,
  options: tRenewalBreakdownOptions,
): string {
  const label = options.leadAgentLabel?.trim();
  return label ? label : `Lead Agent ${tier}`;
}

/**
 * Transforms the typed renewal projection wire data into the modal's display
 * view-model. All money comes from `projection` (cents + currency); the Lead
 * Agent line is appended from `leadAgentLine` and flagged `isNew`. The first
 * charge carries the backend's `prorated` / `estimated` flags so the component
 * can show the "estimado" legend only when Stripe will finalize the amount.
 */
export function buildRenewalBreakdown(
  projection: tRenewalProjectionWireData,
  options: tRenewalBreakdownOptions = {},
): tRenewalBreakdown {
  const { currency } = projection;

  const planLines: tRenewalBreakdownLine[] = projection.lines.map((line) => ({
    description: describeRenewalLine(line.name, line.quantity),
    // RET-1491: the line amount is the line TOTAL = unit × quantity. The wire
    // sends `amountCents` as the per-unit price plus `quantity` separately, so a
    // multi-seat line ("Asiento extra ×2") must multiply — otherwise it shows the
    // unit price while the label says ×N. Non-seat lines are quantity=1 (no-op).
    // The renewal total stays `projectedTotalCents` (already Σ unit×qty), so the
    // per-line amounts now reconcile to it.
    amount: formatPrice({ price: line.amountCents * line.quantity, currency }),
    isNew: false,
  }));

  const leadAgentLine: tRenewalBreakdownLine = {
    description: resolveLeadAgentLabel(projection.leadAgentLine.tier, options),
    amount: formatPrice({
      price: projection.leadAgentLine.amountCents,
      currency,
    }),
    isNew: true,
  };

  return {
    renewalLines: [...planLines, leadAgentLine],
    renewalTotal: formatPrice({
      price: projection.projectedTotalCents,
      currency,
    }),
    firstCharge: {
      amount: formatPrice({
        price: projection.firstCharge.amountCents,
        currency,
      }),
      date: formatPaymentDate(projection.firstCharge.date),
      kind: projection.firstCharge.kind,
      months: projection.firstCharge.months,
      prorated: projection.firstCharge.prorated,
      estimated: projection.firstCharge.estimated,
    },
    renewalDate: formatPaymentDate(projection.renewalDate),
  };
}

/** Formats the fixed $0.00 "today" amount in the preview's currency. */
export function formatZeroToday(currency: string): string {
  return formatPrice({ price: 0, currency });
}

/**
 * Recurring product types that represent the Lead Agent add-on already living on
 * the subscription, as `lines[].type` echoes them.
 *
 * The type that actually arrives is `LEAD_AGENT`. Verified against production on
 * 2026-09-01: `product_types` has one Lead Agent row (`LEAD_AGENT`) and every
 * one of the 24 tier products hangs off it, so no payload can carry
 * `LEAD_AGENT_TRIAL` — that member exists only in leadbilling's PHP enum, with
 * no catalog row behind it. It stays listed here because the enum still declares
 * it and a catalog row would make it real without touching this file; matching a
 * type that cannot arrive costs nothing, missing one that starts arriving
 * double-counts money.
 *
 * The corollary matters more than the naming: a workspace still IN its trial has
 * no Lead Agent row in purchased_products at all, so its projection carries NO
 * Lead Agent line and nothing to dedupe. The dedupe is load-bearing only AFTER
 * the add-on has been bought.
 */
const LEAD_AGENT_LINE_TYPES = ['LEAD_AGENT', 'LEAD_AGENT_TRIAL'];

/**
 * Renewal breakdown for the UPGRADE modal (C5). Same shape and presentation as
 * `buildRenewalBreakdown`, with one correction that the acceptance/reactivation
 * flows do not need.
 *
 * WHY THIS EXISTS — the projection endpoint (A6) was built for workspaces that do
 * NOT own the Lead Agent yet: it composes the recurring `lines` from
 * purchased_products and then ADDS `leadAgentLine` for the requested tier. A
 * workspace that is upgrading already has a live LEAD_AGENT row, so the raw
 * payload lists the OLD tier as a plan line and the NEW tier as the Lead Agent
 * line, and `projectedTotalCents` counts the add-on twice.
 *
 * The correction is deliberately a SUBTRACTION, not a re-derivation: the total
 * stays the backend's authoritative number minus exactly the duplicated term.
 * That keeps the front out of the pricing formula (new line types, reprices and
 * discounts all keep flowing from the backend) and, crucially, it degrades to a
 * no-op the day the backend stops emitting the live Lead Agent line — there is no
 * second place to remember to change. Durable fix lives in
 * `GetRenewalProjectionUseCase::composeTiersLines`; this compensates until then.
 */
export function buildUpgradeRenewalBreakdown(
  projection: tRenewalProjectionWireData,
  options: tRenewalBreakdownOptions = {},
): tRenewalBreakdown {
  const liveLeadAgentCents = projection.lines
    .filter((line) => LEAD_AGENT_LINE_TYPES.includes(line.type))
    .reduce((carry, line) => carry + line.amountCents * line.quantity, 0);

  const deduped: tRenewalProjectionWireData = {
    ...projection,
    lines: projection.lines.filter(
      (line) => !LEAD_AGENT_LINE_TYPES.includes(line.type),
    ),
    // Clamped at 0 so a malformed payload can never render a negative total.
    projectedTotalCents: Math.max(
      0,
      projection.projectedTotalCents - liveLeadAgentCents,
    ),
  };

  return buildRenewalBreakdown(deduped, options);
}
